export const SITUACAO_GUIA = {
  Autorizada: 'A',
  AguardandoAnexo: 'S',
  EmAnalise: 'R',
  Negada: 'N',
  NaoProcessada: 'M',
  InconsistenciaConvenio: 'I',
  ParcialmenteAutorizada: 'Z',
  AguardandoBiometria: 'H',
  BiometriaInvalida: 'T',
  Cancelada: 'C',
  AguardandoCancelamento: 'X',
  Desconhecido: 'Ç',
  BeneficiarioInvalido: 'V',
  AguardandoExecucao: 'W',
  ParcialmenteExecutada: 'P',
  Executada: 'E',
  AguardandoToken: 'O',
  AguardandoAssinatura: 'B',
} as const

export type SituacaoGuia = (typeof SITUACAO_GUIA)[keyof typeof SITUACAO_GUIA]

export type StatusTone = 'success' | 'info' | 'warning' | 'danger' | 'neutral'

export interface StatusMeta {
  label: string
  tone: StatusTone
}

export const situacaoGuiaMeta: Record<SituacaoGuia, StatusMeta> = {
  A: { label: 'Autorizada', tone: 'success' },
  Z: { label: 'Parcialmente autorizada', tone: 'success' },
  E: { label: 'Executada', tone: 'success' },
  P: { label: 'Parcialmente executada', tone: 'success' },
  R: { label: 'Em análise', tone: 'info' },
  B: { label: 'Aguardando assinatura', tone: 'warning' },
  M: { label: 'Não processada', tone: 'warning' },
  N: { label: 'Negada', tone: 'danger' },
  T: { label: 'Biometria inválida', tone: 'danger' },
  V: { label: 'Beneficiário inválido', tone: 'danger' },
  I: { label: 'Inconsistência no convênio', tone: 'danger' },
  S: { label: 'Aguardando anexo', tone: 'neutral' },
  H: { label: 'Aguardando biometria', tone: 'neutral' },
  O: { label: 'Aguardando token', tone: 'neutral' },
  W: { label: 'Aguardando execução', tone: 'neutral' },
  X: { label: 'Aguardando cancelamento', tone: 'neutral' },
  C: { label: 'Cancelada', tone: 'neutral' },
  Ç: { label: 'Desconhecido', tone: 'neutral' },
}

const ORDEM_SITUACAO: SituacaoGuia[] = [
  SITUACAO_GUIA.AguardandoAnexo,
  SITUACAO_GUIA.AguardandoAssinatura,
  SITUACAO_GUIA.AguardandoBiometria,
  SITUACAO_GUIA.AguardandoCancelamento,
  SITUACAO_GUIA.AguardandoExecucao,
  SITUACAO_GUIA.AguardandoToken,
  SITUACAO_GUIA.Autorizada,
  SITUACAO_GUIA.BeneficiarioInvalido,
  SITUACAO_GUIA.BiometriaInvalida,
  SITUACAO_GUIA.Cancelada,
  SITUACAO_GUIA.Desconhecido,
  SITUACAO_GUIA.EmAnalise,
  SITUACAO_GUIA.Executada,
  SITUACAO_GUIA.InconsistenciaConvenio,
  SITUACAO_GUIA.Negada,
  SITUACAO_GUIA.NaoProcessada,
  SITUACAO_GUIA.ParcialmenteAutorizada,
  SITUACAO_GUIA.ParcialmenteExecutada,
]

export const situacaoGuiaOptions = ORDEM_SITUACAO.map((value) => ({
  value,
  label: situacaoGuiaMeta[value].label,
  tone: situacaoGuiaMeta[value].tone,
}))

export const SITUACOES_ATENCAO: SituacaoGuia[] = [
  SITUACAO_GUIA.InconsistenciaConvenio,
  SITUACAO_GUIA.Negada,
  SITUACAO_GUIA.NaoProcessada,
  SITUACAO_GUIA.BiometriaInvalida,
  SITUACAO_GUIA.BeneficiarioInvalido,
]

export const TIPO_GUIA = {
  SpSadt: 'S',
  Consulta: 'C',
  Internacao: 'I',
  Prorrogacao: 'P',
} as const

export type TipoGuia = (typeof TIPO_GUIA)[keyof typeof TIPO_GUIA]

export const tipoGuiaLabel: Record<TipoGuia, string> = {
  S: 'SP/SADT',
  C: 'Consulta',
  I: 'Internação',
  P: 'Prorrogação',
}

export const tipoGuiaOptions = (Object.keys(tipoGuiaLabel) as TipoGuia[]).map((value) => ({
  value,
  label: tipoGuiaLabel[value],
}))

export const situacaoProcedimentoMeta: Record<string, StatusMeta> = {
  A: { label: 'Autorizado', tone: 'success' },
  N: { label: 'Negado', tone: 'danger' },
  P: { label: 'Pendente', tone: 'warning' },
  R: { label: 'Em análise', tone: 'info' },
  C: { label: 'Cancelado', tone: 'neutral' },
  X: { label: 'Aguardando cancelamento', tone: 'warning' },
}

const fallbackMeta = (
  codigo: string | null | undefined,
  nome: string | null | undefined,
): StatusMeta => ({ label: nome || codigo || 'Não informado', tone: 'neutral' })

export const resolveSituacaoGuia = (
  situacao: string | null | undefined,
  nomeSituacao?: string | null,
): StatusMeta => {
  const meta = situacao ? situacaoGuiaMeta[situacao as SituacaoGuia] : undefined
  if (!meta) return fallbackMeta(situacao, nomeSituacao)
  return nomeSituacao ? { ...meta, label: nomeSituacao } : meta
}

export const resolveSituacaoProcedimento = (
  situacao: string | null | undefined,
  nomeSituacao?: string | null,
): StatusMeta => {
  const meta = situacao ? situacaoProcedimentoMeta[situacao] : undefined
  if (!meta) return fallbackMeta(situacao, nomeSituacao)
  return nomeSituacao ? { ...meta, label: nomeSituacao } : meta
}

export const resolveTipoGuia = (
  tipoGuia: string | null | undefined,
  nomeTipoGuia?: string | null,
): string => {
  if (nomeTipoGuia) return nomeTipoGuia
  const label = tipoGuia ? tipoGuiaLabel[tipoGuia as TipoGuia] : undefined
  return label ?? tipoGuia ?? 'Não informado'
}

export const resolveSucessoEvento = (evento: { sucesso: boolean | null }): StatusMeta | null => {
  if (typeof evento.sucesso !== 'boolean') return null
  return evento.sucesso ? { label: 'Sucesso', tone: 'success' } : { label: 'Falha', tone: 'danger' }
}

export const PERFIL = {
  Admin: 'Administrador',
  AdminLegado: 'Admin',
  Operador: 'Operador',
  Supervisor: 'Supervisor',
} as const

export type Perfil = (typeof PERFIL)[keyof typeof PERFIL]

export const SITUACAO_USUARIO = {
  Ativo: 'A',
  Cancelado: 'C',
  Excluido: 'E',
} as const

export type SituacaoUsuario = (typeof SITUACAO_USUARIO)[keyof typeof SITUACAO_USUARIO]

export const PERFIS_ADMINISTRADOR: string[] = [PERFIL.Admin, PERFIL.AdminLegado, PERFIL.Supervisor]
export const REGIME_ATENDIMENTO_OPTIONS = [
  { value: 'E', label: 'Eletivo' },
  { value: 'U', label: 'Urgente' },
]

export const TIPO_ACIDENTE_OPTIONS = [
  { value: '0', label: 'Trânsito' },
  { value: '1', label: 'Trabalho' },
  { value: '2', label: 'Outros' },
  { value: '9', label: 'Não é acidente' },
]

export const TIPO_CONSULTA_OPTIONS = [
  { value: '1', label: 'Primeira consulta' },
  { value: '2', label: 'Retorno' },
  { value: '3', label: 'Pré-natal' },
  { value: '4', label: 'Encaminhamento' },
]

export const UF_CONSELHO_OPTIONS = [
  'AC',
  'AL',
  'AM',
  'AP',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MG',
  'MS',
  'MT',
  'PA',
  'PB',
  'PE',
  'PI',
  'PR',
  'RJ',
  'RN',
  'RO',
  'RR',
  'RS',
  'SC',
  'SE',
  'SP',
  'TO',
].map((uf) => ({ value: uf, label: uf }))

export const TABELA_CREDENCIAL = {
  Tuss: 'TUSS',
  Propria: 'PRÓPRIA',
} as const

export type TabelaCredencial = (typeof TABELA_CREDENCIAL)[keyof typeof TABELA_CREDENCIAL]
